import type {
  TrafficChannel,
  DashboardCell,
  ContractorDashboardRow,
  BranchCardData,
  BranchDashboardGroup,
} from '@ads-manager/shared';
import apiClient from './client';

export interface ContractorChannelItem {
  id: string;
  trafficChannelId: string;
  trafficChannel: TrafficChannel;
}

export interface DashboardSummary {
  totalBudget: number;
  totalSpent: number;
  remaining: number;
  totalLeadsTarget: number;
  totalLeads: number;
  averageCpl: number;
  overallCompletion: number;
  plansCount?: number;
  branchesCount?: number;
}

export const dashboardApi = {
  // Traffic Manager endpoints
  getTrafficManagerDashboard: async (
    year: number,
    month: number,
  ): Promise<DashboardCell[]> => {
    const response = await apiClient.get<DashboardCell[]>(
      '/dashboard/traffic-manager',
      { params: { year, month } },
    );
    return response.data;
  },

  getTrafficManagerByBranch: async (
    year: number,
    month: number,
  ): Promise<BranchDashboardGroup[]> => {
    const response = await apiClient.get<BranchDashboardGroup[]>(
      '/dashboard/traffic-manager/by-branch',
      { params: { year, month } },
    );
    return response.data;
  },

  getTrafficManagerSummary: async (
    year: number,
    month: number,
  ): Promise<DashboardSummary> => {
    const response = await apiClient.get<DashboardSummary>(
      '/dashboard/traffic-manager/summary',
      { params: { year, month } },
    );
    return response.data;
  },

  // Traffic Manager view of contractor dashboard
  getContractorDashboardForTM: async (
    contractorId: string,
    trafficChannelId: string,
    year: number,
    month: number,
  ): Promise<ContractorDashboardRow[]> => {
    const response = await apiClient.get<ContractorDashboardRow[]>(
      `/dashboard/traffic-manager/contractor/${contractorId}`,
      { params: { trafficChannelId, year, month } },
    );
    return response.data;
  },

  getContractorSummaryForTM: async (
    contractorId: string,
    trafficChannelId: string,
    year: number,
    month: number,
  ): Promise<DashboardSummary> => {
    const response = await apiClient.get<DashboardSummary>(
      `/dashboard/traffic-manager/contractor/${contractorId}/summary`,
      { params: { trafficChannelId, year, month } },
    );
    return response.data;
  },

  getContractorChannelsForTM: async (
    contractorId: string,
  ): Promise<ContractorChannelItem[]> => {
    const response = await apiClient.get<ContractorChannelItem[]>(
      `/dashboard/traffic-manager/contractor/${contractorId}/channels`,
    );
    return response.data;
  },

  // Contractor endpoints
  getContractorDashboard: async (
    trafficChannelId: string,
    year: number,
    month: number,
  ): Promise<ContractorDashboardRow[]> => {
    const response = await apiClient.get<ContractorDashboardRow[]>(
      '/dashboard/contractor',
      { params: { trafficChannelId, year, month } },
    );
    return response.data;
  },

  getContractorSummary: async (
    trafficChannelId: string,
    year: number,
    month: number,
  ): Promise<DashboardSummary> => {
    const response = await apiClient.get<DashboardSummary>(
      '/dashboard/contractor/summary',
      { params: { trafficChannelId, year, month } },
    );
    return response.data;
  },

  getContractorChannels: async (): Promise<ContractorChannelItem[]> => {
    const response = await apiClient.get<ContractorChannelItem[]>(
      '/dashboard/contractor/channels',
    );
    return response.data;
  },

  getBranchCardData: async (
    branchId: string,
    trafficChannelId: string,
    year: number,
    month: number,
  ): Promise<BranchCardData> => {
    const response = await apiClient.get<BranchCardData>(
      `/dashboard/contractor/branch/${branchId}`,
      { params: { trafficChannelId, year, month } },
    );
    return response.data;
  },

  getAvailableMonths: async (
    branchId: string,
    trafficChannelId: string,
  ): Promise<Array<{ year: number; month: number }>> => {
    const response = await apiClient.get<Array<{ year: number; month: number }>>(
      `/dashboard/contractor/branch/${branchId}/available-months`,
      { params: { trafficChannelId } },
    );
    return response.data;
  },

  // Traffic Manager view of branch card (read-only)
  getBranchCardDataForTM: async (
    contractorId: string,
    branchId: string,
    trafficChannelId: string,
    year: number,
    month: number,
  ): Promise<BranchCardData> => {
    const response = await apiClient.get<BranchCardData>(
      `/dashboard/traffic-manager/contractor/${contractorId}/branch/${branchId}`,
      { params: { trafficChannelId, year, month } },
    );
    return response.data;
  },

  // Toggle "в работе" status for a contractor branch plan
  toggleWorkingStatus: async (
    branchId: string,
    trafficChannelId: string,
    year: number,
    month: number,
    isWorking: boolean,
  ): Promise<{ isWorking: boolean }> => {
    const response = await apiClient.patch<{ isWorking: boolean }>(
      `/dashboard/contractor/branch/${branchId}/working`,
      { isWorking },
      { params: { trafficChannelId, year, month } },
    );
    return response.data;
  },

  // Toggle "в работе" status for a contractor branch plan (TM view)
  toggleWorkingStatusForTM: async (
    contractorId: string,
    branchId: string,
    trafficChannelId: string,
    year: number,
    month: number,
    isWorking: boolean,
  ): Promise<{ isWorking: boolean }> => {
    const response = await apiClient.patch<{ isWorking: boolean }>(
      `/dashboard/traffic-manager/contractor/${contractorId}/branch/${branchId}/working`,
      { isWorking },
      { params: { trafficChannelId, year, month } },
    );
    return response.data;
  },
};

export default dashboardApi;
